import React, { useRef, useEffect, useState, useMemo } from "react";
import * as d3 from "d3";
import { debounce, fill, update } from "lodash";
import jetpackLogo from "./assets/JetpackAI.png";
import bsky from "./assets/bsky.svg";
import instagram from "./assets/instagram.svg";
import twitter from "./assets/twitter.svg";

const NameViz = ({ text, dirty, data, color, country, showStats, backgroundColor }) => {
  const chartRef = useRef(null);
  const [width, setWidth] = useState(1066);
  const [name, setName] = useState(null);
  const [svg, setSvg] = useState(null);

  const heightScale = d3.scaleLinear()
    .range([200, 1000])
    .domain([0, 1])

  const updateViz = () => {
    if(name) {
      const wdth = 200;
      const heights = data?.heights
      name.selectAll(".letter")
        .data((text || (dirty ? "_" : "Type a name _")).split(""))
        .join(
          enter => enter
            .append("span")
            .attr("class", d => d === "_" ? "letter blink" : "letter")
            .html(d => d)
            .style("font-variation-settings", (d, i) => `'wdth' ${wdth}, 'HGHT' 200`),
          update => update
            .html(d => d)
            .transition()
            .duration(500)
            .attr("class", d => d === "_" ? "letter blink" : "letter")
            .delay((d, i) => i * 100)
            .style("font-variation-settings", (d, i) => `'wdth' ${wdth}, 'HGHT' ${heights ? heightScale(heights[i]) : 200}`),
          exit => exit.remove())

    // name.selectAll(".letter")
    //   .each((d, i, f) => console.log("letter", d, f[i].getBoundingClientRect()))

    const box = name.node().getBoundingClientRect()
    svg.selectAll("rect")
      .attr("width", box.width)
      .attr("height", box.height)
      .attr("x", box.x)
      .attr("y", 0)

    const baseLineRatio = 0.6

    if(data && showStats) {
      svg.transition()
        .style("display", "block")
        .transition()
        .delay(500)
        .attr("opacity", 1)

      svg.select(".baseline")
        .attr("x1", box.x)
        .attr("x2", box.x + box.width)
        .attr("y1", box.height * baseLineRatio)
        .attr("y2", box.height * baseLineRatio)
        .style("opacity", 0.4)
  
      svg.select(".extendedAxis")
        .attr("x1", box.x - 20)
        .attr("x2", box.x + box.width + 20)
        .attr("y1", box.height * 0.95)
        .attr("y2", box.height * 0.95)
        .attr("stroke", color.font)
        .style("opacity", 1)

      svg.select(".axisLabel")
        .attr("x", box.x + box.width)
        .attr("y", box.height * 0.05)
        .attr("font-size", 11)
        .attr("fill", color.font)
        .text(`Yearly count of babies (${country === "fr" ? "France" : "USA"})`)
        .style("opacity", 1)

      svg.select(".credit")
        .attr("fill", color.font)

      svg.select(".jetpack")
        .attr("y", box.height + 30)
        .attr("x", width * 0.5 - (box.height*0.4/2))
        .attr("width", box.height*0.4)

      svg.select(".credit")
        .attr("y", ((box.height)*1.15) + 30 )

      svg.select(".socialNetworks")
        .attr('transform', `translate(${width * 0.5}, ${ ((box.height)*1.15) + 45})`)

      const xScale = d3
        .scaleLinear()
        .domain(d3.extent(data.trends, d => d.year))
        .range([0, box.width]);

      const yScale = d3
        .scaleLinear()
        .domain([0, d3.max(data.trends, d => d.count)])
        .range([box.height * 0.5, 0]);

      const xAxis = d3.axisBottom(xScale)
        .ticks(5)
        .tickSizeOuter(0)
        .tickFormat(d => d);

      const yMargin = 45
      const yAxis = d3.axisRight(yScale)
        .ticks(4)
        .tickSize(box.width + yMargin);

      const trend = svg.select(".trend")
        .attr("transform", `translate(${box.x}, ${box.height * 0.1})`)

      trend.select(".xAxis")
        .attr("transform", `translate(0, ${box.height * 0.85})`)
        .call(xAxis);
      trend.select(".yAxis")
        .attr("transform", `translate(${-yMargin}, 0)`)
        .call(yAxis)
        .call(g => g.select(".domain").remove())
        .call(g => g.selectAll(".tick line")
          .attr("stroke-opacity", 0.5)
          .attr("stroke-dasharray", "2,2"))
        .call(g => g.selectAll(".tick text")
          .attr("x", 4)
          .attr("dy", -4));

      const line = d3
        .line()
        .x((d) => xScale(d.year))
        .y((d) => yScale(d.count))
        .curve(d3.curveBasis);

      const backCurve = trend.select(".backLine")
        //.style("mix-blend-mode", "multiply")
        .attr("stroke", "grey")
        .attr("stroke-width", 5)
        .attr("fill", "none")
        .attr("d", line(data.trends));

      const curve = trend.select(".line")
        //.style("mix-blend-mode", "multiply")
        .attr("stroke", "white")
        .attr("stroke-width", 2)
        .attr("fill", "none")
        .attr("d", line(data.trends))
       
      var length = curve.node()?.getTotalLength();
      curve.attr("stroke-dasharray", length + " " + length)
        .attr("stroke-dashoffset", length)
        .transition()
          .delay(500)
          .duration(1250)
          .attr("stroke-dashoffset", 0);

      backCurve.attr("stroke-dasharray", length + " " + length)
        .attr("stroke-dashoffset", length)
        .transition()
          .delay(500)
          .duration(1250)
          .attr("stroke-dashoffset", 0);
      } else {
        svg
          .transition()
          .delay(100)
          .attr("opacity", 0)

        svg
          .transition()
          .delay(400)
          .style("display", "none")
      }
    }
  } 
  useEffect(() => {
    updateViz()
  }, [text, dirty, data, name, color, showStats]);

  useEffect(() => {
    if(!width) return
    const chart = d3.select(chartRef.current);
    chart.selectAll("*").remove();

    const mainDiv = chart.append('div')
      .attr("width", width)
      .attr("height", width * 0.3)
      .style("display", "flex")
      .style("justify-content", "center")

    const nameDiv = mainDiv.append('span')
      .attr("id", "nameLetters")
      .style("font-family", "Flexible")
      .style("font-size", `${width * ( width < 1200 ? 0.3 : 0.15)}px`)

    setName(nameDiv)

    const heightRatio = (width < 1200 ? 0.5 : 0.3)
    const svgElem = chart.append('svg')
      .attr("width", width)
      .attr("height", width * heightRatio * 1.5)
      .style("position", "absolute")
      .style("top", 0)
      .style("padding-top", '10vh')
      .style("padding-bottom", '10vh')

    setSvg(svgElem)

    const trend = svgElem.append("g")
      .attr("class", "trend")

    // svgElem
    //   .attr("opacity", showStats ? 1 : 0)

    trend
      .append("g")
      .attr("class", "xAxis")
    trend
      .append("g")
      .attr("class", "yAxis");

    trend
      .append("path")
      .attr("class", "backLine")

    trend
      .append("path")
      .attr("class", "line")

    svgElem.append("line")
      .attr("class", "baseline")
      .attr("x1", 0)
      .attr("x2", width)
      .attr("y1", 0)
      .attr("y2", 0)
      .attr("stroke", "grey")
      .attr("opacity", 0)
      .attr("stroke-width", 1)
      .attr("stroke-dasharray", "3,3")

    svgElem.append("line")
      .attr("class", "extendedAxis")
      .attr("x1", 0)
      .attr("x2", width)
      .attr("y1", 0)
      .attr("y2", 0)
      .attr("opacity", 0)
      .attr("stroke-width", 1)
      .attr("font-size", width * 0.05)

    svgElem.append("text")
      .attr("class", "axisLabel")
      .attr("x", 0)
      .attr("y", 0)
      .attr("font-size", 11)
      .attr("fill", "white")
      .attr("text-anchor", "end")
      .attr("opacity", 0)
      .text(`Yearly count of babies (${country === "fr" ? "France" : "USA"})`)

    const credit = svgElem.append('g')
    const jetpack = credit.append('a')
      .attr('href', 'https://jetpack.ai')
      .attr('target', '_blank')
      .style('pointer-events', 'initial')
  
    jetpack.append("image")
      .attr("class", "jetpack")
      .attr("xlink:href", jetpackLogo)
      .attr("x", width * 0.5 - (width*0.07/2))
      .attr("y", width)
      .attr("width", width*0.07)

    credit.append("text")
      .attr("class", "credit")
      .attr("x", width*0.5)
      .attr("y", width * (width < 1200 ? 0.4 : 0.24))
      .attr("opacity", 1)
      .attr("fill", "white")
      .attr("font-size", 11)
      .attr("text-anchor", "middle")
      .append("tspan")
      .text("Experiment by Karim Douieb")
    
    const sn = credit.append("g")
      .attr("class", "socialNetworks")
      .style("textColor", color)
      .attr('transform', `translate(${width * 0.5}, ${ width * (width < 1200 ? 0.405 : 0.245)})`)

    sn.append("a")
      .attr('href', 'https://bsky.app/profile/karimdouieb.bsky.social')
      .attr('target', '_blank')
      .style('pointer-events', 'initial')
      .append('image')
      .attr('xlink:href', bsky)
      .attr('x', -19)
      .attr('y', 0)
      .attr('width', 15)
      .attr('height', 15)
      .attr('fill', 'white')
      .attr("opacity", 0.7)
      .style("color", color)

    sn.append("a")
      .attr('href', 'https://www.instagram.com/karim_douieb/')
      .attr('target', '_blank')
      .style('pointer-events', 'initial')
      .append('image')
      .attr('xlink:href', instagram)
      .attr('x', 0)
      .attr('y', 0)
      .attr('width', 15)
      .attr('height', 15)
      .attr('fill', 'white')
      .attr("opacity", 0.7)

    sn.append("a")
      .attr('href', 'https://x.com/karim_douieb')
      .attr('target', '_blank')
      .style('pointer-events', 'initial')
      .append('image')
      .attr('xlink:href', twitter)
      .attr('x', 18)
      .attr('y', 0)
      .attr('width', 15)
      .attr('height', 15)
      .attr('fill', 'red')
      .style("textColor", "red")
      .style("fill", "red")
      .style("color", "red")
      .attr("opacity", 0.7)

    updateViz() 
  }, [width]);

  useEffect(() => {
    function updateWidth() {
      setWidth(chartRef.current?.clientWidth || 0);
    }
    const handleResize = debounce(updateWidth, 500);
    updateWidth();

    window.addEventListener("resize", updateWidth)// handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return <div id="nameViz" ref={chartRef} style={{width: "100vw", position: "relative", backgroundColor, paddingTop: '10vh', paddingBottom: '10vh'}}>
  </div>;
};

export default NameViz;